import { useToast } from 'primevue/usetoast'
import type { ToastType } from '@shared/types/toast'

const DEFAULT_DURATION_MS = 4000

const SEVERITY_MAP: Record<ToastType, 'success' | 'error' | 'warn' | 'info'> = {
  success: 'success',
  error: 'error',
  warning: 'warn',
  info: 'info',
}

export interface AppToastPayload {
  /** Severity preset. */
  type: ToastType
  /** Body copy (PrimeVue `detail`). */
  message: string
  /** Optional bold header above `message` (PrimeVue `summary`). */
  title?: string
  /** Auto-dismiss timeout in ms. Default 4000. */
  duration?: number
}

export function useAppToast() {
  const toast = useToast()

  function addToast({ type, message, title, duration = DEFAULT_DURATION_MS }: AppToastPayload) {
    toast.add({
      severity: SEVERITY_MAP[type],
      summary: title,
      detail: message,
      life: duration,
    })
  }

  // Convenience wrappers — `(title, message)` shape used across containers.
  const showSuccess = (title: string, message: string) =>
    addToast({ type: 'success', title, message })
  const showError = (title: string, message: string) => addToast({ type: 'error', title, message })
  const showWarning = (title: string, message: string) =>
    addToast({ type: 'warning', title, message })
  const showInfo = (title: string, message: string) => addToast({ type: 'info', title, message })

  return { addToast, showSuccess, showError, showWarning, showInfo }
}
